import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface UseCardDragOptions {
  /** Ref to the scrollable content column inside the card. */
  contentRef: React.RefObject<HTMLDivElement>;
  minVh?: number;      // default 15
  maxVh?: number;      // default 98
  initialVh?: number;  // default 30
  /** Called whenever card height changes (for parent alignment, etc.). */
  onHeightChange?: (px: number) => void;
}

export interface UseCardDragResult {
  heightPx: number;
  /** Attach to the card root via onPointerDownCapture and to drag-bar / header via onPointerDown. */
  onPointerDown: (e: React.PointerEvent) => void;
  isDragging: boolean;
  /** Ensure the card is at least this viewport height (vh), clamped to min/max bounds. */
  ensureMinHeightVh: (vh: number) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const BOTTOM_CONTROLS_HEIGHT = 0;
const ADMIT_THRESHOLD = 8; // px before we commit to a gesture direction

type GestureMode = 'pending' | 'drag' | 'scroll' | 'none';

const SUPPRESS_NATIVE_SELECTION_SELECTOR = [
  '.install-fab',
  '.drag-bar',
  '.details-card-header',
  '.details-sticky-nav',
  '.details-nav-row',
  '.nav-tab',
  '.audio-guide-row',
  '.audio-guide-btn',
  '.primary-media-slot',
  '.slider-horizontal',
].join(',');

const NATIVE_INTERACTION_SELECTOR = [
  'input',
  'textarea',
  'select',
  'option',
  'iframe',
  '[contenteditable="true"]',
].join(',');

function availableHeight(): number {
  return (typeof window !== 'undefined' ? window.innerHeight : 800) - BOTTOM_CONTROLS_HEIGHT;
}

function vhToPx(vh: number): number {
  return (vh / 100) * availableHeight();
}

function clearNativeSelection() {
  const selection = document.getSelection?.();
  if (selection && selection.rangeCount > 0) {
    selection.removeAllRanges();
  }
}

function shouldSuppressNativeSelection(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest(NATIVE_INTERACTION_SELECTOR)) return false;
  return !!target.closest(SUPPRESS_NATIVE_SELECTION_SELECTOR);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export default function useCardDrag(opts: UseCardDragOptions): UseCardDragResult {
  const {
    contentRef,
    minVh = 15,
    maxVh = 98,
    initialVh = 30,
    onHeightChange,
  } = opts;

  const [heightPx, setHeightPx] = useState<number>(() => vhToPx(initialVh));
  const [isDragging, setIsDragging] = useState(false);

  // --- Internal refs (all 8 from the original, encapsulated) ---------------
  const draggingRef = useRef(false);
  const startYRef = useRef(0);
  const startXRef = useRef(0);
  const startHeightRef = useRef(0);
  const gestureModeRef = useRef<GestureMode>('none');
  const startScrollTopRef = useRef(0);
  const insideContentStartRef = useRef(false);
  const recentMovesRef = useRef<Array<{ y: number; time: number }>>([]);
  const pointerDownElementRef = useRef<Element | null>(null);
  const previousBodyUserSelectRef = useRef<string | null>(null);
  const previousBodyWebkitUserSelectRef = useRef<string | null>(null);

  // Keep a ref-copy of heightPx so the effect closure always reads the latest.
  const heightRef = useRef(heightPx);
  heightRef.current = heightPx;

  // Fire callback when height changes
  const onHeightChangeRef = useRef(onHeightChange);
  onHeightChangeRef.current = onHeightChange;

  useEffect(() => {
    if (typeof onHeightChangeRef.current === 'function') {
      onHeightChangeRef.current(heightPx);
    }
  }, [heightPx]);

  const ensureMinHeightVh = useCallback(
    (vh: number) => {
      if (!Number.isFinite(vh)) return;
      const clampedVh = Math.max(minVh, Math.min(maxVh, vh));
      const minRequiredPx = vhToPx(clampedVh);
      setHeightPx((current) => (current < minRequiredPx ? minRequiredPx : current));
    },
    [minVh, maxVh],
  );

  // --- Pointer-down handler (called from JSX) ------------------------------
  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      pointerDownElementRef.current = e.currentTarget;

      if (shouldSuppressNativeSelection(e.target)) {
        e.preventDefault();
        clearNativeSelection();
      }

      startYRef.current = e.clientY;
      startXRef.current = e.clientX;
      startHeightRef.current = heightRef.current;
      recentMovesRef.current = [{ y: e.clientY, time: performance.now() }];

      // If gesture starts on a horizontal carousel, treat as outside content
      const startedInsideCarousel = (e.target as HTMLElement).closest?.(
        '.sights-carousel-container',
      );

      insideContentStartRef.current =
        !!contentRef.current &&
        contentRef.current.contains(e.target as Node) &&
        !startedInsideCarousel;

      if (insideContentStartRef.current && contentRef.current) {
        startScrollTopRef.current = contentRef.current.scrollTop;
      }

      gestureModeRef.current = 'pending';
    },
    [contentRef],
  );

  // --- Global pointer-move / pointer-up (single useEffect) -----------------
  useEffect(() => {
    const minPx = () => vhToPx(minVh);
    const maxPx = () => vhToPx(maxVh);
    const midPx = () => vhToPx((minVh + maxVh) / 2);

    function beginDrag(pointerId: number) {
      gestureModeRef.current = 'drag';
      draggingRef.current = true;
      setIsDragging(true);
      try { pointerDownElementRef.current?.setPointerCapture?.(pointerId); } catch {}
      if (previousBodyUserSelectRef.current === null) {
        previousBodyUserSelectRef.current = document.body.style.userSelect;
        previousBodyWebkitUserSelectRef.current = document.body.style.webkitUserSelect;
      }
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
      clearNativeSelection();
    }

    function handleMove(e: PointerEvent) {
      // ---- Gesture disambiguation ----
      if (gestureModeRef.current === 'pending') {
        const dx = e.clientX - startXRef.current;
        const dy = startYRef.current - e.clientY; // positive = finger up

        if (Math.abs(dy) > ADMIT_THRESHOLD && Math.abs(dy) > Math.abs(dx) * 1.1) {
          // Vertical intent confirmed
          if (insideContentStartRef.current && contentRef.current) {
            const el = contentRef.current;
            const atTop = el.scrollTop <= 0;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight;
            const wantsScrollDown = dy > 0;
            const canScroll = (wantsScrollDown && !atBottom) || (!wantsScrollDown && !atTop);

            if (canScroll) {
              gestureModeRef.current = 'scroll';
            } else {
              beginDrag(e.pointerId);
            }
          } else {
            beginDrag(e.pointerId);
          }
        } else if (Math.abs(dx) > ADMIT_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
          // Horizontal → ignore (carousel handles it)
          gestureModeRef.current = 'none';
        }
      }

      // ---- Manual scroll mode ----
      if (gestureModeRef.current === 'scroll') {
        const dy = startYRef.current - e.clientY;
        const el = contentRef.current;
        if (el) {
          const newScroll = startScrollTopRef.current + dy;
          el.scrollTop = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, newScroll));
        }
        e.preventDefault();
        return;
      }

      // ---- Drag-to-resize mode ----
      if (!draggingRef.current) return;
      e.preventDefault();
      e.stopPropagation();

      const dy = startYRef.current - e.clientY;
      let newHeight = startHeightRef.current + dy;
      newHeight = Math.max(minPx(), Math.min(maxPx(), newHeight));
      setHeightPx(newHeight);

      // Track recent moves for flick velocity (last ~120ms)
      const now = performance.now();
      recentMovesRef.current.push({ y: e.clientY, time: now });
      while (recentMovesRef.current.length > 2 && now - recentMovesRef.current[0].time > 120) {
        recentMovesRef.current.shift();
      }
    }

    function stopDrag(e: PointerEvent) {
      if (draggingRef.current) {
        e?.stopPropagation();

        // ---- Flick-to-snap ----
        const moves = recentMovesRef.current;
        if (moves.length >= 2) {
          const first = moves[0];
          const last = moves[moves.length - 1];
          const dy = last.y - first.y;           // +ve = downwards
          const dt = last.time - first.time || 1;
          const velocity = dy / dt;              // px/ms

          const FLICK_UP = -0.7;
          const FLICK_DOWN_HIGH = 1.0;
          const FLICK_DOWN_MED = 0.7;

          let target: number | null = null;
          if (velocity < FLICK_UP) target = maxPx();
          else if (velocity > FLICK_DOWN_HIGH) target = minPx();
          else if (velocity > FLICK_DOWN_MED) target = midPx();

          if (target !== null) setHeightPx(target);
        }
      }

      draggingRef.current = false;
      gestureModeRef.current = 'none';
      setIsDragging(false);
      try {
        const pointerElement = pointerDownElementRef.current;
        if (pointerElement?.hasPointerCapture?.(e.pointerId)) {
          pointerElement.releasePointerCapture(e.pointerId);
        }
      } catch {}
      pointerDownElementRef.current = null;
      document.body.style.userSelect = previousBodyUserSelectRef.current ?? '';
      document.body.style.webkitUserSelect = previousBodyWebkitUserSelectRef.current ?? '';
      previousBodyUserSelectRef.current = null;
      previousBodyWebkitUserSelectRef.current = null;
    }

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', stopDrag);
    window.addEventListener('pointercancel', stopDrag);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', stopDrag);
      window.removeEventListener('pointercancel', stopDrag);
    };
  }, [contentRef, minVh, maxVh]);

  return { heightPx, onPointerDown, isDragging, ensureMinHeightVh };
}
